import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { ApiUrl } from 'Services/Services';
import { FaEye } from "react-icons/fa6";
import { useNavigate } from 'react-router-dom';
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

function Food() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [foodList, setFoodList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ingredients, setIngredients] = useState([]); // fetched ingredients
  const [selectedIngredients, setSelectedIngredients] = useState([]); // selected ingredient IDs
  const navigate = useNavigate();

  const [newFood, setNewFood] = useState({
    name: '',
    price: '',
    category: '',
    image: null,
    imagePreview: '',
    description: '',
    variations: [{ name: '', price: '' }],
    toppings: [{ name: '', price: '' }],
  });

  const fetchCategory = async () => {
    try {
      const res = await axios.get(`${ApiUrl}/category`);
      setCategories(res.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchIngredients = async () => {
    try {
      const res = await axios.get(`${ApiUrl}/ingredients`);
      setIngredients(res.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchFood = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/food`);
      setFoodList(response.data.foods.reverse());
    } catch (error) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    fetchFood();
    fetchCategory();
    fetchIngredients();
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = foodList?.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(foodList?.length / itemsPerPage);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image') {
      const file = files[0];
      setNewFood((prev) => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file),
      }));
    } else {
      setNewFood((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddField = (type) => {
    setNewFood((prev) => ({
      ...prev,
      [type]: [...prev[type], ''],
    }));
  };

  const handleChangeField = (type, idx, value) => {
    const updated = [...newFood[type]];
    updated[idx] = value;
    setNewFood((prev) => ({ ...prev, [type]: updated }));
  };

  const handleRemoveField = (type, idx) => {
    const updated = newFood[type].filter((_, i) => i !== idx);
    setNewFood((prev) => ({ ...prev, [type]: updated }));
  };

  const toggleIngredientSelection = (id) => {
    setSelectedIngredients((prev) =>
      prev.includes(id) ? prev.filter((ingId) => ingId !== id) : [...prev, id]
    );
  };

  const handleAddFood = async () => {
    if (!newFood.image) {
      toast.error("Food image is required");
      return;
    }
    if (selectedIngredients.length === 0) {
      toast.error("Please select at least one ingredient");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", newFood.name);
      formData.append("price", newFood.price);
      formData.append("image", newFood.image);
      formData.append("categoryId", newFood.category);
      formData.append("description", newFood.description || "");
      formData.append("ingredients", JSON.stringify(selectedIngredients));

      const foodResponse = await axios.post(`${ApiUrl}/food`, formData);
      const foodId = foodResponse.data?.food?.id;

      if (!foodId) throw new Error("Failed to create food");

      const validVariations = newFood.variations.filter(v => v.name && v.price !== '');
      await Promise.all(
        validVariations.map((variation) =>
          axios.post(`${ApiUrl}/variation`, { ...variation, foodId })
        )
      );

      const validToppings = newFood.toppings.filter(t => t.name && t.price !== '');
      await Promise.all(
        validToppings.map((topping) =>
          axios.post(`${ApiUrl}/topping`, { ...topping, foodId })
        )
      );

      fetchFood();
      setIsModalOpen(false);
      toast.success('Food Item Created');
      resetFoodForm();
    } catch (error) {
      console.error("Add Food Error:", error.message);
      toast.warn(error.message);
    }
  };

  const resetFoodForm = () => {
    setNewFood({
      name: '',
      price: '',
      category: '',
      description: '',
      image: null,
      imagePreview: '',
      variations: [],
      toppings: [],
    });
    setSelectedIngredients([]);
  };

  const handleEdit = (id) => navigate(`/admin/editfood/${id}`);
  const handelView = (id) => navigate(`/admin/viewFood/${id}`);
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${ApiUrl}/food/${id}`);
      toast.success('Item Deleted');
      fetchFood();
    } catch (error) {
      toast.warn(error.message);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-end mb-6">
        <button
          onClick={() => {navigate('/admin/addFood')}}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          + Add Food
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg">
        <table className="min-w-full border bg-white">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-2 px-4">Image</th>
              <th className="py-2 px-4">Name</th>
              <th className="py-2 px-4">Price</th>
              <th className="py-2 px-4">Category</th>
              <th className="py-2 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((food) => (
              <tr key={food.id} className="border-t">
                <td className="py-2 px-4">
                  <img src={food.image || 'https://via.placeholder.com/60'} alt="food" className="w-14 h-14 rounded" />
                </td>
                <td className="py-2 px-4">{food.name}</td>
                <td className="py-2 px-4">Rs. {food.price}</td>
                <td className="py-2 px-4">{food?.category?.title || '—'}</td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button onClick={() => handelView(food.id)} className="text-blue-500"><FaEye /></button>
                  <button onClick={() => handleEdit(food.id)} className="text-blue-500"><FaEdit /></button>
                  <button onClick={() => handleDelete(food.id)} className="text-red-500"><FaTrash /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex justify-end items-center gap-4 mt-4">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`px-3 py-1 border rounded ${currentPage === 1 ? 'bg-gray-200 text-gray-400' : 'hover:bg-gray-100'}`}
          >
            Prev
          </button>
          <span className="text-sm">Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 border rounded ${currentPage === totalPages ? 'bg-gray-200 text-gray-400' : 'hover:bg-gray-100'}`}
          >
            Next
          </button>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm"></div>
          <div className="relative z-50 bg-white w-full max-w-2xl rounded-xl p-6 shadow-xl overflow-y-auto max-h-[90vh]">
            <h2 className="text-xl font-semibold">Add New Food</h2>
            <input name="name" placeholder="Food Name" value={newFood.name} onChange={handleChange} className="w-full border px-4 py-2 rounded-md" />
            <input name="price" placeholder="Price" type="number" value={newFood.price} onChange={handleChange} className="w-full border px-4 py-2 rounded-md mt-2" />

            <select name="category" value={newFood.category} onChange={handleChange} className="w-full border px-4 py-2 rounded-md bg-white mt-2">
              <option value="">Select Category</option>
              {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.title}</option>)}
            </select>

            <input type="file" name="image" accept="image/*" onChange={handleChange} className="w-full border px-2 py-1 rounded-md mt-2" />
            {newFood.imagePreview && <img src={newFood.imagePreview} alt="food preview" className="w-32 h-32 object-cover rounded-md border mt-2" />}

            <Editor
              editorState={editorState}
              onEditorStateChange={(state) => {
                setEditorState(state);
                setNewFood(prev => ({
                  ...prev,
                  description: draftToHtml(convertToRaw(state.getCurrentContent())),
                }));
              }}
              wrapperClassName="border rounded-md mt-3"
              editorClassName="px-4 min-h-[150px]"
              toolbarClassName="border-b"
            />

            {/* Ingredient selection */}
            <div className="mt-4">
              <h3 className="font-medium">Select Ingredients</h3>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {ingredients.map((ing) => (
                  <label key={ing.id} className="flex items-center gap-2 border p-2 rounded-md cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIngredients.includes(ing.id)}
                      onChange={() => toggleIngredientSelection(ing.id)}
                    />
                    <span>{ing.name} ({ing.quantity} {ing.unit})</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Variations */}
            <div className="mt-4">
              <h3 className="font-medium">Variations</h3>
              {newFood.variations.map((item, idx) => (
                <div key={idx} className="flex gap-2 mt-2">
                  <input value={item.name} onChange={(e) => handleChangeField('variations', idx, { ...item, name: e.target.value })} placeholder="Name" className="flex-1 border px-3 py-2 rounded-md" />
                  <input value={item.price} type="number" onChange={(e) => handleChangeField('variations', idx, { ...item, price: e.target.value })} placeholder="Price" className="w-28 border px-3 py-2 rounded-md" />
                  <button onClick={() => handleRemoveField('variations', idx)} className="text-red-500 text-lg">×</button>
                </div>
              ))}
              <button onClick={() => handleAddField('variations')} className="text-blue-500 mt-2 hover:underline text-sm">+ Add Variation</button>
            </div>

            {/* Toppings */}
            <div className="mt-4">
              <h3 className="font-medium">Toppings</h3>
              {newFood.toppings.map((item, idx) => (
                <div key={idx} className="flex gap-2 mt-2">
                  <input value={item.name} onChange={(e) => handleChangeField('toppings', idx, { ...item, name: e.target.value })} placeholder="Name" className="flex-1 border px-3 py-2 rounded-md" />
                  <input value={item.price} type="number" onChange={(e) => handleChangeField('toppings', idx, { ...item, price: e.target.value })} placeholder="Price" className="w-28 border px-3 py-2 rounded-md" />
                  <button onClick={() => handleRemoveField('toppings', idx)} className="text-red-500 text-lg">×</button>
                </div>
              ))}
              <button onClick={() => handleAddField('toppings')} className="text-blue-500 mt-2 hover:underline text-sm">+ Add Topping</button>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">Cancel</button>
              <button onClick={handleAddFood} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">Add Food</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Food;
